import Link from 'next/link';
import cx from 'classnames';
import styles from './genericLink.module.scss';
import { External } from '../Icons';
import SiteContext from '../AppContext';
import { useContext, forwardRef } from 'react';

/**
 * React component to handle links
 * @param {object} [props] component props
 * @param {string} [props.internalLink] internal link slug
 * @param {string} [props.externalLink] external link URL
 * @param {string} [props.jumpLink] link to element on the current page
 * @param {string} [props.className] class to be used in link
 * @param {JSX.element} [props.children] children of the link
 * @param {boolean} [props.externalIcon] Property to add or remove external icon
 * @param {JSX.Element} [props.AppendedIcon] optional parameter to append icon to the link
 * @param {string} [props.ariaLabel] optional text for the aria label attribute
 * @param {boolean} [props.disabled] disabled attribute
 * @param {Function} [props.onClick] on click handler
 * @param {Function} [props.onKeydown] on key down handler
 * @param {string} [props.target] target to be used in the external link
 * @param {object} [ref] component ref
 * @returns {JSX.element} Cta Link React component
 */
function GenericLink(
  {
    internalLink,
    externalLink,
    jumpLink,
    className,
    children,
    AppendedIcon,
    ariaLabel,
    externalIcon = true,
    disabled = false,
    internalLinkTarget = '_self',
    target = '_blank',
    onKeydown,
    onClick,
  },
  ref,
) {
  let as = '';
  const siteContext = useContext(SiteContext);
  const isCircular = internalLink === '[Circular]';
  const href = internalLink?.fields?.slug ? '/[locale]/[...slug]' : '/[locale]';

  if (internalLink && !isCircular && internalLink.fields) {
    // eslint-disable-next-line max-len
    as = `/${siteContext.locale}/${internalLink.fields.slug ? internalLink.fields.slug : ''}`;
  }

  return internalLink && !isCircular ? (
    <Link
      href={href}
      as={as}
      className={cx(className, styles.link)}
      aria-label={ariaLabel}
      role={onClick ? 'button' : null}
      disabled={disabled}
      onClick={onClick}
      target={internalLinkTarget}
      onKeyDown={onKeydown}
      ref={ref}>
      <span dir={'auto'} className={styles.wrapper} tabIndex="-1">
        {children}
      </span>
      {AppendedIcon}
    </Link>
  ) : (
    <a
      href={externalLink ?? jumpLink}
      className={cx(className, styles.link)}
      target={target}
      rel="noreferrer"
      onClick={onClick}
      onKeyDown={onKeydown}
      ref={ref}
      aria-label={ariaLabel}
      disabled={disabled}>
      <span dir={'auto'} className={styles.wrapper} tabIndex="-1">
        {children}
      </span>
      {AppendedIcon}
      {externalIcon && <External />}
    </a>
  );
}

export default forwardRef(GenericLink);
